<template>
    <div>
        <activix-modal
            size="xl"
            :name="name"
            :portal="portal"
            :sticky-footer="true"
            :color="'gray'"
            :closable="modalClosable"
            ref="modal"
            @close="onClose"
            @closed="onClosed"
            @before-open="onBeforeOpen"
        >
            <template #header>
                <h4 class="modal-title">
                    {{ modalTitle }}
                </h4>
            </template>

            <template #body>
                <next-automation-alert :lead="lead" />

                <email-composer-imap-alert :lead="lead" :account="contextAccount" />

                <communicating-alert
                    assignment-trace-source="desktop_email_notify"
                    :lead="lead"
                    :communicating-type="communicatingType"
                    :communicating-user="communicatingUser"
                    :recent-communication="recentCommunication"
                    :most-recent-communication="mostRecentCommunication"
                    :mass-mailing-leads-removed="massMailingLeadsRemoved"
                    :mass-mailing-leads-do-not-market="massMailingLeadsDoNotMarket"
                    @dismiss="dismiss"
                    @close="close"
                />

                <composer-loading v-if="loading" />

                <lead-xpress-multilingual
                    :ai-draft-enabled="emailAiDraftEntryPointEnabled"
                    :class="{ loading: communicatingUser && !ownCommunication }"
                    data-testid="email-lead-modal"
                    :communicating="massMailing ? false : communicating"
                    :communicating-user="communicatingUser"
                    :current-lang="currentLang"
                    :draft="draft"
                    :is-forwarding="isForwarding"
                    :lead="lead"
                    :last-email-body="lastEmailBody"
                    :sender="effectiveSender"
                    :signatures-by-locale="signaturesByLocale"
                    :signature-removed-by-user="signatureRemovedByUser"
                    :unsubscribe-link="unsubscribeLinkByLocale"
                    :mass-mailing-lead-count="massMailingLeadCount"
                    :response-templates="responseTemplates"
                    :ai-draft-state="aiDraftState"
                    :ai-draft-feedback="aiDraftFeedback"
                    :ai-draft-generated-draft="aiDraftGeneratedDraft"
                    :ai-draft-has-generated-draft="aiDraftHasGeneratedDraft"
                    :ai-draft-rate-limit-seconds="aiDraftRateLimitSeconds"
                    :ai-draft-service-lead-disabled="aiDraftServiceLeadDisabled"
                    :ai-draft-can-undo-by-locale="aiDraftCanUndoByLocale"
                    :can-generate-ai-draft-by-locale="canGenerateAiDraftByLocale"
                    :can-update-ai-draft-by-locale="canUpdateAiDraftByLocale"
                    :ai-draft-update-disabled-reason-by-locale="aiDraftUpdateDisabledReasonByLocale"
                    :campaign-settings="campaignSettings"
                    :activity-feed-reply-context="activityFeedReplyContext"
                    :customer-profile-output="customerProfileOutput"
                    :customer-profile-email-context="customerProfileEmailContext"
                    ref="leadXpress"
                    @change-lang="changeLanguage"
                    @set-initial-body="setInitialBody"
                    @change-locale="changeLocale"
                    @generate-ai-draft="generateEmailAiDraft"
                    @update-ai-draft="updateEmailAiDraft"
                    @undo-ai-draft="undoEmailAiDraft"
                    @stop-ai-draft="stopEmailAiDraft"
                    @select-ai-draft-feedback="selectEmailAiDraftFeedback"
                    @submit-ai-draft-feedback="submitEmailAiDraftFeedback"
                    @update:original-communication="originalCommunicationHtml = $event"
                    @video-cancelled="handleVideoCancelled"
                    v-model:response="response"
                    v-model:add-last-email-body="addLastEmailBody"
                    v-show="!loading"
                />
            </template>

            <template #footer>
                <div
                    class="flex flex-col-reverse gap-3 | xs:flex-row xs:items-center xs:justify-between"
                    v-if="useSimplifiedEmailAiDraftFooter"
                >
                    <activix-button
                        data-testid="email-ai-discard-draft-button"
                        :disabled="!!communicatingUser"
                        @click="discardEmailAiDraft"
                    >
                        <span class="inline-flex items-center">
                            <icon class="mr-2 h-4 w-4" :name="$icons.trash" />
                            {{ $t('mailClient.aiDraft.discardDraft') }}
                        </span>
                    </activix-button>

                    <activix-tooltip :content="sendTooltip">
                        <span class="w-full | xs:w-auto">
                            <activix-button
                                data-testid="email-ai-send-button"
                                class="w-full"
                                type="primary"
                                :loading="sending"
                                :disabled="disabledSendButton"
                                @click="send(lead.id)"
                            >
                                <icon class="mr-2" name="regular/send-email" />
                                {{ $t('mailClient.send') }}
                            </activix-button>
                        </span>
                    </activix-tooltip>
                </div>

                <div
                    class="flex flex-col justify-between space-y-4 | xs:space-y-0 xs:flex-row"
                    v-else
                >
                    <div class="flex space-x-4">
                        <activix-button @click="cancelCampaignCreation" v-if="isCampaign">
                            {{ $t('general.cancel') }}
                        </activix-button>
                        <span class="w-full | xs:w-auto">
                            <activix-button
                                class="w-full"
                                data-testid="email-lead-clear-draft-button"
                                @click="reset()"
                            >
                                <icon :name="$icons.loading" class="mr-2" />
                                {{ $t('mailClient.aiDraft.clearDraft') }}
                            </activix-button>
                        </span>
                        <activix-tooltip :content="deleteDraftTooltip" v-if="draft">
                            <span class="w-full | xs:w-auto">
                                <activix-button
                                    class="w-full"
                                    :disabled="!!communicatingUser"
                                    @click="clearDraft(lead.id)"
                                >
                                    <span class="inline-flex items-center text-red-500">
                                        <icon :name="$icons.trash" class="mr-2" />
                                        {{ $t('mailClient.draft') }}
                                    </span>
                                </activix-button>
                            </span>
                        </activix-tooltip>
                    </div>

                    <div class="flex space-x-4">
                        <span class="w-full | xs:w-auto">
                            <activix-button
                                class="w-full"
                                data-testid="email-lead-save-draft-button"
                                :disabled="!!communicatingUser"
                                @click="triggerSave"
                                v-if="!sending && !massMailing"
                            >
                                <icon class="mr-2" name="regular/pencil-1" />
                                {{ $t('mailClient.draft') }}
                            </activix-button>
                        </span>

                        <activix-tooltip :content="sendTooltip">
                            <span class="w-full | xs:w-auto">
                                <activix-button
                                    class="w-full"
                                    :disabled="disabledSendButton"
                                    type="primary"
                                    @click="triggerCampaignReview(false)"
                                    v-if="isCampaign"
                                >
                                    {{ $t('campaign.reviewCampaign') }}
                                </activix-button>
                                <activix-button
                                    class="w-full"
                                    data-testid="email-lead-send-button"
                                    type="primary"
                                    :loading="sending"
                                    :disabled="disabledSendButton"
                                    @click="send(lead.id)"
                                    v-else
                                >
                                    <icon class="mr-2" name="regular/send-email" />
                                    {{ $t('mailClient.send') }}
                                </activix-button>
                            </span>
                        </activix-tooltip>
                    </div>
                </div>
            </template>
        </activix-modal>

        <activix-confirm-modal
            type="warning"
            :content="missingMultilingualDataText"
            portal="modal-2"
            @approve="sendWithoutMultilingual"
            @closed="modals.missingLocale.opened = false"
            v-model:opened="modals.missingLocale.opened"
        />
    </div>
</template>

<script>
    import TemplateType from '@/entities/TemplateType.js';

    // Utils
    import { camelCase, cloneDeep } from 'lodash-es';
    import { ref } from 'vue';
    import { mapActions, mapState } from 'pinia';
    import { getUnrecoverableRecipientListMessage } from '@/api/leads.js';
    import { showWarning } from '@/utils/toastr.js';
    import { getContentAccountLogo, getContentEmailSignature } from '@/utils/translations.ts';
    import { normalizeEmailDraft } from '@/components/lead_xpress/utils/normalizeEmailDraft.ts';
    import {
        replaceSignature,
        bodyContainsSignature,
    } from '@/components/lead_xpress/utils/signatureReplacement.js';
    import {
        getComposerBodyContent,
        replaceComposerBodyContent,
    } from '@/components/lead_xpress/aiDraft/composerBody.ts';
    import { composerHtmlToPlainText } from '@/components/lead_xpress/aiDraft/draftBodyHtml.ts';
    import { useEmailAiDraft } from '@/components/lead_xpress/aiDraft/useEmailAiDraft.ts';
    import { createEmailAiDraftApiAdapter } from '@/components/lead_xpress/aiDraft/emailAiDraftApiAdapter.ts';
    import ActivixDate from '@/value-objects/ActivixDate.js';
    import { useTimedPageAction } from '@/hooks/useTimedPageAction.ts';
    import TimedPageActions from '@/constants/TimedPageActions.ts';
    import {
        filterActivityFeedEmailRecipients,
        normalizeEmailAddress,
        reconcileActivityFeedEmailRecipients,
    } from '@/components/customerProfile/activityFeedEmailRecipients.ts';
    import { invalidateCustomerProfileLocally } from '@/components/customerProfile/invalidation/customerProfileInvalidation.ts';

    // Pinia
    import { useAccountStore } from '@/store/modules/account/store.js';
    import { useUserStore } from '@/store/modules/user/store.js';
    import { useDashboardStore } from '@/store/modules/dashboard/store.js';
    import { useClientCardStore } from '@/store/modules/clientCard/store.js';
    import { useContextStore } from '@/store/modules/context/store.ts';
    import { useGlobalStore } from '@/store/store.ts';

    // Components
    import ComposerLoading from '@/components/loading/ComposerLoading.vue';
    import { useEventBus } from '@/hooks/useEventBus.ts';
    import LeadXpressMultilingual from '../lead_xpress/LeadXpressMultilingual.vue';
    import NextAutomationAlert from '../lead/NextAutomationAlert.vue';
    import CommunicatingAlert from '../CommunicatingAlert.vue';
    import EmailComposerImapAlert from '../EmailComposerImapAlert.vue';

    // Entities
    import CommunicationMethod from '../../entities/CommunicationMethod.js';
    import CommunicationType from '../../entities/CommunicationType.js';
    import Lead from '../../entities/Lead.js';

    const resolveEmailModalLead = (leadId, preparedLead, customerProfileOutput, fetchLead) => {
        if (preparedLead) {
            if (String(preparedLead.id) !== String(leadId)) {
                throw new Error('Customer Email target does not match modal Lead');
            }
            return preparedLead;
        }

        if (customerProfileOutput?.lead) {
            if (String(customerProfileOutput.lead.id) !== String(leadId)) {
                throw new Error('Customer Profile output Lead does not match modal Lead');
            }
            return customerProfileOutput.lead;
        }

        return fetchLead(leadId);
    };

    const buildCustomerEmailRecipients = (context, selectedIds) => {
        const recipientsById = new Map(
            context.recipients.map((recipient) => [recipient.contact_email_id, recipient])
        );
        if (selectedIds.some((id) => !recipientsById.has(id))) {
            return null;
        }

        return selectedIds.map((id) => {
            const recipient = recipientsById.get(id);
            return {
                id: recipient.contact_email_id,
                contact_email_id: recipient.contact_email_id,
                contact_id: recipient.contact_id,
                email: recipient.destination,
                valid: true,
                preferred: true,
                validated_email: {
                    is_disposable: false,
                    risk_score: 0,
                },
            };
        });
    };

    const buildEmailDraftRecipients = (contactContext, selectedRecipients) => {
        if (!contactContext) {
            return selectedRecipients;
        }
        const addressesById = new Map(
            contactContext.recipients.map((recipient) => [
                recipient.contact_email_id,
                recipient.destination,
            ])
        );
        return selectedRecipients
            .map((recipient) => addressesById.get(recipient.contact_email_id))
            .filter((email) => typeof email === 'string');
    };

    const EMAIL_AI_UPDATE_BODY_MAX_LENGTH = 2000;
    const EMAIL_AI_UPDATE_SUBJECT_MAX_LENGTH = 255;

    const shouldSkipEmailSend = (context, leadId, retryCount) =>
        (context.sending && retryCount === 0) || (!context.massMailing && !leadId);

    const shouldShowMissingLocaleModal = (context, useOneLocale) =>
        context.massMailing &&
        !context.multilingualDataComplete() &&
        !useOneLocale &&
        (context.localePreference === 'leadLocale' ||
            context.incompleteLocales.includes(context.localePreference));

    const prepareEmailVideo = async (context) => {
        if (!['uploaded', 'processed'].includes(context.videoCommunication.status)) {
            return true;
        }

        try {
            await context.createLandingPage();
            await context.$ziggeo.updateZiggeoVideo(context.videoCommunication.ziggeoId, {
                tags: 'sent',
            });
            context.$ziggeo.resetVideo(context.lead.id);
        } catch (error) {
            if (error.response && error.response.status === 403) {
                context.close();
            }

            context.$notify.error(
                context.$t('communications.video.statusMessage.videoCommunicationFail')
            );
            context.response.videoProcessed = false;
            context.response.ziggeoId = '';
            context.sending = false;
            context.submitted = false;
            context.sent = false;

            return false;
        }

        return true;
    };

    const appendEmailBody = (context, data) => {
        data.body = context.response.body;

        Object.keys(data.body).forEach((locale) => {
            if (!context.templateAdvanced) {
                data.body[locale] = replaceSignature({
                    body: data.body[locale],
                    newSignature: context.signaturesByLocale[locale] || '',
                    oldSignature: '',
                    locale,
                    users:
                        context.contextAccount?.users || context.contextAccount?.activeUsers || [],
                    currentUser: context.currentUser,
                    authUser: context.authUser,
                    skipInsertionWhenAbsent: context.signatureRemovedByUser?.[locale] || false,
                });
                data.body[locale] = context.addItemsToBody(data.body[locale], locale);
            }

            data.body[locale] = data.body[locale].replaceAll('contenteditable="true"', '');
            data.body[locale] = context.stripSkeletonSignature(data.body[locale]);
            if (context.addLastEmailBody && context.lastEmailBody) {
                data.body[locale] += `
                            <div><br></div>
                            ${context.lastEmailBody}
                        `;
            }
            if (context.originalCommunicationHtml) {
                data.body[locale] += context.originalCommunicationHtml;
            }
        });
    };

    const appendEmailAttachments = (context, payload) => {
        Object.entries(context.response.attachments).forEach(([key, attachmentsByLocale]) => {
            attachmentsByLocale.forEach((attachment) => {
                if (attachment.id) {
                    payload.append(`existingAttachments[${key}][]`, JSON.stringify(attachment));
                } else {
                    payload.append(`attachments[${key}][]`, attachment);
                }
            });
        });
    };

    const appendBlacklistedLeadIds = (context, payload) => {
        if (!context.allLeadsInTableSelected) {
            return;
        }

        context.blackListedLeadIds.forEach((blackListedLeadId) => {
            payload.append('blackListedLeadIds[]', blackListedLeadId);
        });
    };

    const buildEmailPayload = (context) => {
        const payload = new FormData();
        const data = {};

        data.subject = context.response.subject;
        data.isForwarding = context.isForwarding;

        if (context.isForwarding) {
            data.to = [];
            data.forwardTo = context.response.forwardTo;
        } else {
            data.to = context.massMailing ? [] : context.response.to.map((email) => email.id);
            data.forwardTo = [];
        }

        appendEmailBody(context, data);

        data.currentLang = context.currentLang.toLowerCase();
        data.cc = context.response.cc;
        data.bcc = context.response.bcc;
        data.videoCommunication = {
            ziggeoId: context.response.ziggeoId,
            landingPageSid: context.landingPageSid,
        };
        data.category = context.response.category;
        data.template = context.response.template;
        data.response_template_id = empty(context.response.template)
            ? null
            : context.response.template.id;
        data.locale = context.currentLang.toLowerCase();
        data.localePreference = context.massMailing
            ? context.localePreference
            : context.currentLang.toLowerCase();
        data.from = context.response.from;

        if (context.customerProfileOutput) {
            data.customerProfileOutput = {
                customer_id: context.customerProfileOutput.customerId,
                contact_email_id: context.customerProfileOutput.contactEmailId,
            };
        }

        const interactionId = context.emailAiDraft?.interactionIdByLocale?.value?.[data.locale];

        if (!context.massMailing && Number.isInteger(interactionId) && interactionId > 0) {
            data.ai_interaction_id = interactionId;
        }

        if (context.customerProfileEmailContext) {
            data.recipient_scope = context.customerProfileEmailContext.recipient_scope;
            data.contact_id = context.customerProfileEmailContext.contact_id;
            data.contact_email_recipients = context.response.to.map((email) => ({
                recipient_kind: 'contact_email',
                contact_email_id: email.contact_email_id,
            }));
        }

        if (context.needFromBackup) {
            data.from_backup = context.response.fromBackup;
        }

        payload.append('data', JSON.stringify(data));
        payload.append('accountId', context.contextAccount.id);
        appendEmailAttachments(context, payload);
        appendBlacklistedLeadIds(context, payload);

        return payload;
    };

    const handleSuccessfulEmailSend = async (
        context,
        leadId,
        customerId,
        draftVersionAtSend,
        attributed
    ) => {
        context.sent = true;

        if (!context.massMailing && (!attributed || draftVersionAtSend)) {
            if (draftVersionAtSend) {
                context.deleteDraft(leadId, { draftVersionAtSend });
            } else {
                context.deleteDraft(leadId);
            }
        }

        context.displaySuccessNotification();

        if (context.taskEventToBeCompleted) {
            await context.$axios
                .post(`v1/reminder/done/${context.taskEventToBeCompleted}`)
                .then(({ data }) => {
                    if (!data.success) {
                        return;
                    }

                    context.taskEventUpdated(data.taskEvent);
                    invalidateCustomerProfileLocally({
                        customerId,
                        payloads: ['activity'],
                    });
                });
        }

        if (!context.customerProfileOutput) {
            context.setContextLeadIdAction(leadId);
        }

        context.$nextTick(() => {
            context.close();
            context.sending = false;
        });
    };

    const handleFailedEmailSend = (context, error, payload) => {
        const leadCount = context.massMailing ? context.massMailingLeadCount : 1;

        if (error.response) {
            switch (error.response.status) {
                case 400:
                    showWarning(
                        context.$t(
                            'emailLead.invalidEmail',
                            { lead: context.lead.fullName },
                            leadCount
                        )
                    );
                    context.sent = false;
                    context.sending = false;
                    return;

                case 403:
                    showWarning(context.$t('emailLead.cantSendEmail', leadCount));
                    context.sent = false;
                    context.sending = false;
                    return;

                case 406:
                    showWarning(context.$t('emailLead.unsubscribeRequired'));
                    context.sent = false;
                    context.sending = false;
                    return;

                case 422:
                    showWarning(
                        getUnrecoverableRecipientListMessage(error) ??
                            context.$t('emailLead.cantSendSameEmail')
                    );
                    context.sent = false;
                    context.sending = false;
                    return;
            }

            context.appendNewError({
                code: '0099',
                display: true,
                error,
                request: { payload },
            });
            context.sent = false;
        } else if (String(error).includes('Error: Network Error')) {
            showWarning(context.$t('toastr.errorNetwork'), 0);
            context.sent = false;
        } else {
            context.displaySuccessNotification();
            context.close();
        }

        context.sending = false;
    };

    const submitEmail = async (context, payload, leadId, customerId) => {
        const locale = context.currentLang.toLowerCase();
        const interactionId = context.emailAiDraft?.interactionIdByLocale?.value?.[locale];
        const attributed =
            !context.massMailing && Number.isInteger(interactionId) && interactionId > 0;

        try {
            const response = await context.$api.leads.email(context.query, payload);
            const draftVersionAtSend = response?.data?.draft_version_at_send ?? null;
            context.emailAiDraft?.reset?.(false, locale);
            await handleSuccessfulEmailSend(
                context,
                leadId,
                customerId,
                draftVersionAtSend,
                attributed
            );
        } catch (error) {
            handleFailedEmailSend(context, error, payload);
        }
    };

    export default {
        emits: ['reviewCampaign', 'canceled'],

        components: {
            ComposerLoading,
            LeadXpressMultilingual,
            NextAutomationAlert,
            CommunicatingAlert,
            EmailComposerImapAlert,
        },

        props: {
            name: {
                type: String,
                required: true,
            },
            isForwarding: {
                type: Boolean,
                default: false,
            },
            portal: {
                type: String,
                default: 'modal-0',
            },
            closable: {
                type: Boolean,
                default: true,
            },
            campaignSettings: {
                type: Object,
                required: false,
            },
        },

        setup() {
            const eventBus = useEventBus();
            const aiDraftLeadId = ref(null);
            const emailAiDraft = useEmailAiDraft({
                adapter: createEmailAiDraftApiAdapter(() => aiDraftLeadId.value),
            });

            return { emailAiDraft, eventBus, aiDraftLeadId };
        },

        data() {
            return {
                draft: null,
                nextStep: 'sendEmail',
                response: {
                    category: null,
                    template: null,
                    subject: {
                        fr: '',
                        en: '',
                    },
                    to: [],
                    attachments: {
                        fr: [],
                        en: [],
                    },
                    templateAttachments: [],
                    body: {
                        fr: '',
                        en: '',
                    },
                    design: {
                        fr: null,
                        en: null,
                    },
                    civility: {
                        id: '',
                        title: '',
                    },
                    cc: [],
                    bcc: [],
                    forwardTo: [],
                    from: '',
                    fromBackup: '',
                    ziggeoId: '',
                    videoProcessed: false,
                },
                signatureRemovedByUser: {
                    fr: false,
                    en: false,
                },
                landingPageSid: null,
                attachmentErrors: [],
                communicatingUser: null,
                communicatingType: null,
                recentCommunication: null,
                mostRecentCommunication: null,
                modalSession: 0,
                oldCommunicationRequest: 0,
                massMailingLeadsRemoved: null,
                communicating: false,
                sent: false,
                sending: false,
                discardingEmailAiDraft: false,
                lastEmailBody: '',
                currentLang: this.$i18n.getContentLanguage() == 'fr' ? 'FR' : 'EN',
                submitted: false,
                lead: new Lead(),
                leadId: null,
                loading: true,
                localePreference: 'leadLocale',
                addLastEmailBody: false,
                originalCommunicationHtml: '',
                incompleteLocales: [],
                modals: {
                    missingLocale: {
                        opened: false,
                    },
                },
                triggerSource: null,
                skipNextDraftSave: false,
                activityFeedReplyContext: null,
                customerProfileOutput: null,
                customerProfileEmailContext: null,
                customerProfileEmailPreselectedIds: [],
            };
        },

        computed: {
            ...mapState(useGlobalStore, ['taskEventToBeCompleted']),
            ...mapState(useDashboardStore, {
                massMailing: 'isMassMailing',
                dashboardCriteria: 'dashboardCriteria',
                blackListedLeadIds: 'blackListedLeadIds',
                bulkSelect: 'bulkSelect',
                pagination: 'pagination',
                allLeadsInTableSelected: 'allLeadsInTableSelected',
            }),
            ...mapState(useClientCardStore, ['replyingEmail']),

            parentAuthUser() {
                return useUserStore().parentAuthUser;
            },

            authUser() {
                return useUserStore().authUser;
            },

            contextAccount() {
                return useAccountStore().contextAccount;
            },

            customCategories() {
                return useAccountStore().configs.customCategories.map((category) => ({
                    id: category.id,
                    locale_name: this.getCategoryLocaleName(category),
                }));
            },

            signaturesByLocale() {
                return {
                    fr: this.signature('fr'),
                    en: this.signature('en'),
                };
            },

            aiDraftState() {
                return this.emailAiDraft.state.value;
            },

            aiDraftFeedback() {
                return this.emailAiDraft.feedback.value;
            },

            aiDraftGeneratedDraft() {
                return this.emailAiDraft.generatedDraft.value;
            },

            aiDraftHasGeneratedDraft() {
                return this.emailAiDraft.hasGeneratedDraft.value;
            },

            aiDraftRateLimitSeconds() {
                return this.emailAiDraft.rateLimitSeconds.value;
            },

            aiDraftCanUndoByLocale() {
                return {
                    fr: this.emailAiDraft.canUndoForLocale('fr'),
                    en: this.emailAiDraft.canUndoForLocale('en'),
                };
            },

            aiDraftServiceLeadDisabled() {
                return this.isEmailAiDraftServiceLead();
            },

            emailAiDraftEntryPointEnabled() {
                const account = this.lead?._account || this.lead?.account;

                if (typeof account?.hasAiDraft === 'function') {
                    return account.hasAiDraft();
                }

                return Boolean(account?.ai_package && account?.ai_draft);
            },

            canGenerateAiDraftByLocale() {
                return {
                    fr: this.canGenerateEmailAiDraftForLocale('fr'),
                    en: this.canGenerateEmailAiDraftForLocale('en'),
                };
            },

            canUpdateAiDraftByLocale() {
                return {
                    fr: this.canUpdateEmailAiDraftForLocale('fr'),
                    en: this.canUpdateEmailAiDraftForLocale('en'),
                };
            },

            aiDraftUpdateDisabledReasonByLocale() {
                return {
                    fr: this.getEmailAiUpdateDisabledReason('fr'),
                    en: this.getEmailAiUpdateDisabledReason('en'),
                };
            },

            effectiveSenderId() {
                const primarySenderId = this.parseUserId(this.response.from);

                if (primarySenderId) {
                    return primarySenderId;
                }

                if (this.response.from && !this.massMailing && this.lead) {
                    const leadSenderId = this.parseUserId(this.lead[this.response.from]);

                    if (leadSenderId) {
                        return leadSenderId;
                    }
                }

                if (this.response.from && this.massMailing) {
                    return null;
                }

                return this.parseUserId(this.response.fromBackup);
            },

            effectiveSender() {
                if (!this.effectiveSenderId) {
                    return null;
                }

                const userStore = useUserStore();
                const currentUser = this.currentUser;
                const activeUsers = this.contextAccount?.activeUsers ?? [];

                if (currentUser?.id === this.effectiveSenderId) {
                    return currentUser;
                }

                return (
                    userStore.getUserById(this.effectiveSenderId) ||
                    activeUsers.find((user) => user.id === this.effectiveSenderId) ||
                    null
                );
            },

            isLoadingSenderData() {
                if (!this.effectiveSenderId) {
                    return false;
                }

                return this.$wait.is('fetching.contextUsers');
            },

            unsubscribeLinkByLocale() {
                return this.unsubscribeLink(this.currentLang.toLowerCase());
            },

            emailData() {
                return {
                    category: this.response.category,
                    subject: {
                        fr: this.response.subject.fr,
                        en: this.response.subject.en,
                    },
                    attachments: {
                        fr: this.response.attachments.fr,
                        en: this.response.attachments.en,
                    },
                    civility: {
                        id: this.response.civility.id,
                        title: this.response.civility.title,
                    },
                    to: this.response.to,
                    cc: this.response.cc,
                    bcc: this.response.bcc,
                    from: this.response.from,
                    from_backup: this.response.fromBackup,
                    body: {
                        fr: this.response.body.fr,
                        en: this.response.body.en,
                    },
                    video_processed:
                        this.response.videoProcessed ||
                        this.videoCommunication.status === 'processed',
                    ziggeo_id: this.response.ziggeoId || this.videoCommunication.ziggeoId,
                    design: {
                        fr: this.response.design.fr,
                        en: this.response.design.en,
                    },
                    template: this.response.template,
                    isForwarding: this.isForwarding,
                    templateAttachments: this.response.templateAttachments,
                    currentLang: this.currentLang.toLowerCase(),
                    localePreference: this.localePreference,
                };
            },

            ownCommunication() {
                return (
                    this.communicatingUser?.id === this.parentAuthUser?.id ||
                    this.communicatingUser?.id === this.authUser.id
                );
            },

            hasFilteredResponseTemplates() {
                return !!this.responseTemplates.length;
            },

            recording() {
                let status = '';

                if (this.response.ziggeoId) {
                    status = this.response.videoProcessed ? 'processed' : 'uploaded';
                }

                return {
                    ziggeoId: this.response.ziggeoId,
                    status,
                };
            },

            missingMultilingualDataText() {
                if (this.incompleteLocales.includes('fr')) {
                    return this.$t('email.incomplete_fr_response');
                }

                return this.$t('email.incomplete_en_response');
            },

            videoCommunication() {
                return this.$ziggeo.getVideoById(this.lead.id) || {};
            },

            hasValidMailOwner() {
                return (
                    Number.isInteger(this.response.from) ||
                    Number.isInteger(this.response.fromBackup)
                );
            },

            needFromBackup() {
                return !Number.isInteger(this.response.from);
            },

            massMailingLeads() {
                if (!this.massMailing || this.allLeadsInTableSelected) {
                    return [];
                }

                return this.bulkSelect.filter(
                    (lead) => lead.emails.length > 0 && !lead.do_not_market
                );
            },

            massMailingLeadsDoNotMarket() {
                if (!this.massMailing || this.allLeadsInTableSelected) {
                    return [];
                }

                return this.bulkSelect.filter((lead) => lead.do_not_market);
            },

            massMailingLeadCount() {
                if (!this.massMailing) {
                    return 0;
                }

                if (!this.allLeadsInTableSelected) {
                    return this.massMailingLeads.length;
                }

                return !this.massMailingLeadsRemoved
                    ? this.pagination.total -
                          this.blackListedLeadIds.length -
                          this.massMailingLeadsDoNotMarket.length
                    : this.pagination.total -
                          this.massMailingLeadsRemoved.length -
                          this.blackListedLeadIds.length -
                          this.massMailingLeadsDoNotMarket.length;
            },

            canSaveDraft() {
                const emptyBody =
                    !this.response.body ||
                    this.response.body == this.initialBody('fr') ||
                    this.response.body == this.initialBody('en');

                if (this.massMailing || this.sending || this.sent || !this.lead.id) {
                    return false;
                }

                if (!this.response.subject && emptyBody) {
                    return false;
                }

                return !(
                    this.draft &&
                    this.draft.body == this.response.body &&
                    this.draft.subject == this.response.subject &&
                    this.draft.from == this.response.from &&
                    this.draft.cc == this.response.cc &&
                    this.draft.bcc == this.response.bcc &&
                    (this.draft.ai_interaction_ids?.en ?? null) ===
                        (this.emailAiDraft?.interactionIdByLocale?.value?.en ?? null) &&
                    (this.draft.ai_interaction_ids?.fr ?? null) ===
                        (this.emailAiDraft?.interactionIdByLocale?.value?.fr ?? null) &&
                    this.draft.video?.ziggeo_id == this.response.ziggeoId &&
                    this.draft.video?.video_processed == this.response.videoProcessed
                );
            },

            authorizedToSend() {
                const locales = ['fr', 'en'];

                for (const locale of locales) {
                    if (this.isAuthorized(locale)) {
                        return true;
                    }
                }

                return false;
            },

            currentUser() {
                return (
                    this.parentAuthUser.childUserForAccount(this.lead.account_id) || this.authUser
                );
            },

            templateAdvanced() {
                return !!this.response.template?.is_advanced;
            },

            modalTitle() {
                if (this.useSimplifiedEmailAiDraftFooter) {
                    return this.$t('mailClient.aiDraft.emailTitle');
                }

                return this.$t('mailClient.composerMessage');
            },

            modalClosable() {
                return this.closable && !this.discardingEmailAiDraft;
            },

            useSimplifiedEmailAiDraftFooter() {
                return (
                    !this.isCampaign &&
                    ['fr', 'en'].some((locale) => this.canUseEmailAiDraftForLocale(locale))
                );
            },

            disableSendReasons() {
                const reasons = [];

                if (!this.authorizedToSend) {
                    reasons.push('notAuthorizedToSend');
                }
                if (this.sending) {
                    reasons.push('sending');
                }
                if (this.aiDraftState === 'loading') {
                    reasons.push('aiDraftLoading');
                }
                if (this.communicating && !this.ownCommunication) {
                    reasons.push('communicating');
                }
                if (this.mostRecentCommunication) {
                    reasons.push('hasMostRecentCommunication');
                }
                if (!this.hasValidMailOwner) {
                    reasons.push('hasNoValidMailOwner');
                }
                if (this.massMailing && this.massMailingLeadCount === 0) {
                    reasons.push('hasNoRecipient');
                }
                if (
                    !empty(this.response.category) &&
                    empty(this.response.template) &&
                    this.hasFilteredResponseTemplates
                ) {
                    reasons.push('emptyTemplate');
                }
                if (['uploading', 'uploaded'].includes(this.videoCommunication.status)) {
                    reasons.push('videoUploading');
                }

                return reasons;
            },

            sendTooltip() {
                const messages = [];

                if (this.disableSendReasons.includes('notAuthorizedToSend')) {
                    if (empty(this.response.from)) {
                        messages.push(this.$t('mailClient.emptySenderTooltip'));
                    }

                    if (this.massMailing) {
                        if (empty(this.response.subject.fr) && empty(this.response.subject.en)) {
                            messages.push(this.$t('mailClient.emptySubjectTooltip'));
                        }

                        if (
                            this.bodyIsEmpty(this.response.body.fr, 'fr') &&
                            this.bodyIsEmpty(this.response.body.en, 'en')
                        ) {
                            messages.push(this.$t('mailClient.emptyBodyTooltip'));
                        }
                    } else {
                        if (empty(this.response.subject)) {
                            messages.push(this.$t('mailClient.emptySubjectTooltip'));
                        }

                        if (empty(this.response.body)) {
                            messages.push(this.$t('mailClient.emptyBodyTooltip'));
                        }
                    }
                }

                if (this.disableSendReasons.includes('hasNoValidMailOwner')) {
                    messages.push(this.$t('mailClient.emptyFallbackSenderTooltip'));
                }

                if (this.disableSendReasons.includes('hasNoRecipient')) {
                    messages.push(this.$t('mailClient.emptyRecipientTooltip'));
                }

                if (this.disableSendReasons.includes('emptyTemplate')) {
                    messages.push(this.$t('mailClient.emptyResponseTemplateTooltip'));
                }

                if (this.disableSendReasons.includes('videoUploading')) {
                    messages.push(this.$t('mailClient.videoUploading'));
                }

                return messages.join('<br>');
            },

            deleteDraftTooltip() {
                if (!this.draft) {
                    return '';
                }

                const draftDate = new ActivixDate(this.draft.created_at).toHumanShort();

                return `
                    <span class="font-semibold">${this.$t('mailClient.draftDate')}</span>
                    <br>
                    ${draftDate}
                `;
            },

            disabledSendButton() {
                return (
                    this.disableSendReasons.filter((reason) => {
                        switch (reason) {
                            case 'emptyTemplate':
                                return false;
                            default:
                                return true;
                        }
                    }).length > 0
                );
            },

            query() {
                if (this.allLeadsInTableSelected) {
                    return { search: this.dashboardCriteria };
                }

                if (this.bulkSelect.length && this.massMailingLeads.length) {
                    return {
                        ids: this.massMailingLeads.map((lead) => lead.id),
                    };
                }

                return { id: this.leadId };
            },

            isCampaign() {
                return this.campaignSettings && Object.keys(this.campaignSettings).length > 0;
            },

            responseTemplates() {
                const account = this.lead.account_id ? this.lead.account : this.contextAccount;

                return account.responseTemplates.filter((template) => {
                    return (
                        template.type === TemplateType.EMAIL &&
                        template.is_active &&
                        template.translations.length
                    );
                });
            },
        },

        watch: {
            '$i18n.locale'(newLocale) {
                this.currentLang = this.$i18n.getContentLanguage(newLocale).toUpperCase();
            },

            massMailingLeadsRemoved(newValue) {
                let tmpLeads = cloneDeep(this.bulkSelect);

                if (!empty(newValue)) {
                    for (let i = newValue.length - 1; i >= 0; i--) {
                        tmpLeads = tmpLeads.filter((tmpLead) => tmpLead.id != newValue[i].id);
                    }
                }

                this.setBulkSelect(tmpLeads);
            },

            currentLang() {
                this.reset(true);
            },

            massMailing(newValue) {
                if (newValue) {
                    this.communicating = false;
                }
            },

            videoCommunication(video = this.videoCommunication) {
                this.response.ziggeoId = video.ziggeoId;
                this.response.videoProcessed = video.status === 'processed';
            },

            'response.body': {
                deep: true,
                handler() {
                    this.trackSignatureRemoval();
                },
            },
        },

        methods: {
            ...mapActions(useDashboardStore, ['setBulkSelect', 'setAllLeadsInTableSelected']),
            ...mapActions(useContextStore, ['setContextLeadIdAction']),
            ...mapActions(useGlobalStore, [
                'updateLeadAction',
                'fetchLead',
                'taskEventUpdated',
                'appendNewError',
                'setTaskEventToBeCompleted',
            ]),

            handleVideoCancelled(token) {
                if (this.draft?.video?.ziggeo_id === token) {
                    this.draft.video = null;
                }

                if (this.response.ziggeoId === token) {
                    this.response.ziggeoId = '';
                    this.response.videoProcessed = false;
                }
            },

            getCategoryLocaleName(category) {
                if (!category.account_id) {
                    return this.$t(`divisions.${camelCase(category.name)}`);
                }

                if (this.contextAccount.id !== category.account_id) {
                    return `[Parent] ${category.name}`;
                }

                return category.name;
            },

            canUseEmailAiDraftForLocale(locale) {
                if (
                    !this.emailAiDraftEntryPointEnabled ||
                    this.massMailing ||
                    this.templateAdvanced ||
                    this.isEmailAiDraftServiceLead() ||
                    (this.communicating && !this.ownCommunication)
                ) {
                    return false;
                }

                return ['fr', 'en'].includes(locale);
            },

            canGenerateEmailAiDraftForLocale(locale) {
                return this.canUseEmailAiDraftForLocale(locale);
            },

            canUpdateEmailAiDraftForLocale(locale) {
                return (
                    this.canUseEmailAiDraftForLocale(locale) &&
                    !this.bodyIsEmpty(this.response.body?.[locale] || '', locale) &&
                    !this.emailAiUpdateSubjectLimitHit(locale) &&
                    !this.emailAiUpdateBodyLimitHit(locale)
                );
            },

            getEmailAiUpdateBodyContent(locale) {
                const suffixSourceHtml =
                    typeof this.initialBody === 'function' ? this.initialBody(locale) : '';

                return getComposerBodyContent(this.response.body?.[locale] || '', {
                    normalizedSuffixHtml: [this.originalCommunicationHtml, suffixSourceHtml],
                    suffixSourceHtml,
                });
            },

            emailAiUpdateBodyLimitHit(locale) {
                return (
                    composerHtmlToPlainText(this.getEmailAiUpdateBodyContent(locale)).length >
                    EMAIL_AI_UPDATE_BODY_MAX_LENGTH
                );
            },

            emailAiUpdateSubjectLimitHit(locale) {
                const subject = this.response.subject?.[locale];

                return (
                    typeof subject === 'string' &&
                    subject.length > EMAIL_AI_UPDATE_SUBJECT_MAX_LENGTH
                );
            },

            getEmailAiUpdateDisabledReason(locale) {
                if (
                    !this.canUseEmailAiDraftForLocale(locale) ||
                    this.bodyIsEmpty(this.response.body?.[locale] || '', locale)
                ) {
                    return null;
                }

                if (this.emailAiUpdateSubjectLimitHit(locale)) {
                    return 'mailClient.aiDraft.updateSubjectLimitHit';
                }

                if (this.emailAiUpdateBodyLimitHit(locale)) {
                    return 'mailClient.aiDraft.updateBodyLimitHit';
                }

                return null;
            },

            isEmailAiDraftServiceLead() {
                return Boolean(this.lead?.isService?.());
            },

            buildEmailAiDraftSnapshot() {
                return {
                    body: cloneDeep(this.response.body),
                    subject: cloneDeep(this.response.subject),
                };
            },

            restoreEmailAiDraftSnapshot(snapshot) {
                this.response.subject = cloneDeep(snapshot.subject);
                this.response.body = cloneDeep(snapshot.body);
            },

            restoreEmailAiDraftLocaleSnapshot(snapshot) {
                this.response.subject[snapshot.locale] = snapshot.subject;
                this.response.body[snapshot.locale] = snapshot.body;
            },

            emailAiDraftSubjectStartsWithReply(locale) {
                return /^\s*re:/i.test(this.response.subject?.[locale] || '');
            },

            emailAiDraftExistingSubject(locale) {
                const subject = this.response.subject?.[locale];

                if (
                    typeof subject !== 'string' ||
                    subject.length > 255 ||
                    !this.emailAiDraftSubjectStartsWithReply(locale)
                ) {
                    return null;
                }

                return subject;
            },

            applyEmailAiDraft({ locale, subject, body, useGeneratedSubject = null }) {
                const shouldUseGeneratedSubject =
                    useGeneratedSubject ?? !this.emailAiDraftSubjectStartsWithReply(locale);

                if (shouldUseGeneratedSubject && subject) {
                    this.response.subject[locale] = subject;
                }

                const suffixSourceHtml =
                    typeof this.initialBody === 'function' ? this.initialBody(locale) : '';

                this.response.body[locale] = replaceComposerBodyContent(
                    this.response.body[locale],
                    body,
                    {
                        normalizedSuffixHtml: [this.originalCommunicationHtml, suffixSourceHtml],
                        suffixSourceHtml,
                    }
                );
            },

            async generateEmailAiDraft(locale) {
                if (!this.canGenerateEmailAiDraftForLocale(locale) || !this.lead?.id) {
                    return;
                }

                await this.emailAiDraft.generate({
                    locale,
                    leadId: Number(this.lead.id),
                    existingSubject: this.emailAiDraftExistingSubject(locale),
                    snapshot: this.buildEmailAiDraftSnapshot(),
                    applyDraft: (draft) => {
                        if (this.communicating && !this.ownCommunication) {
                            return false;
                        }

                        this.applyEmailAiDraft(draft);

                        return true;
                    },
                });
            },

            async updateEmailAiDraft({ locale, instruction }) {
                const trimmedInstruction = (instruction || '').trim();

                if (
                    !this.emailAiDraftEntryPointEnabled ||
                    (this.communicating && !this.ownCommunication) ||
                    !this.lead?.id ||
                    !trimmedInstruction ||
                    !this.canUpdateEmailAiDraftForLocale(locale)
                ) {
                    return;
                }

                await this.emailAiDraft.update({
                    locale,
                    leadId: Number(this.lead.id),
                    snapshot: this.buildEmailAiDraftSnapshot(),
                    subject: this.response.subject?.[locale] || '',
                    body: this.getEmailAiUpdateBodyContent(locale),
                    instruction: trimmedInstruction,
                    applyDraft: (draft) => {
                        if (this.communicating && !this.ownCommunication) {
                            return false;
                        }

                        this.applyEmailAiDraft(draft);

                        return true;
                    },
                });
            },

            undoEmailAiDraft(locale) {
                this.emailAiDraft.undo(locale, (snapshot) =>
                    this.restoreEmailAiDraftLocaleSnapshot(snapshot)
                );
            },

            async stopEmailAiDraft() {
                await this.emailAiDraft.stop((snapshot) =>
                    this.restoreEmailAiDraftSnapshot(snapshot)
                );
            },

            selectEmailAiDraftFeedback(feedback) {
                this.emailAiDraft.selectFeedback(feedback);
            },

            submitEmailAiDraftFeedback(submission) {
                this.emailAiDraft.submitFeedback(submission);
            },

            async setLastEmailBody() {
                if (this.massMailing) {
                    this.lastEmailBody = '';
                    return;
                }

                let sourceCommunicationId = this.activityFeedReplyContext?.sourceCommunicationId;

                if (!sourceCommunicationId) {
                    const communications = this.lead.communications.filter(
                        (c) => c.communication_method_id == CommunicationMethod.EMAIL
                    );
                    sourceCommunicationId = communications[communications.length - 1]?.id;
                }

                if (!sourceCommunicationId) {
                    this.lastEmailBody = '';
                    return;
                }

                const response = await this.$axios.get(
                    `v1/communications/${sourceCommunicationId}`,
                    {
                        include: 'user',
                    }
                );

                const communication = response.data.data;

                if (communication.lead_id != this.lead.id) {
                    this.lastEmailBody = '';
                    return;
                }

                let senderName = this.lead.fullName;
                let senderEmail = communication.client_email
                    ? `&lt;${communication.client_email}&gt;`
                    : '';

                if (communication.communication_type_id == CommunicationType.OUTGOING) {
                    senderName = this.getFullName(communication.user);
                    senderEmail = communication.user_email
                        ? `&lt;${communication.user_email}&gt;`
                        : '';
                }

                if (senderName) {
                    senderName += ' ';
                }

                const date = new ActivixDate(communication.created_at).toHumanShort();

                this.lastEmailBody = `
                    <div class="last-email-body">
                        ${this.$t('email.on')} ${date}, ${senderName}${senderEmail}, ${this.$t(
                    'email.wrote'
                )}:<br>
                        <blockquote type="cite">
                            ${communication.email_body}
                        </blockquote>
                    </div>
                `;
            },

            strippedHtml(content) {
                const regex = /(<([^>]+)>)/gi;

                return content.replace(regex, '');
            },

            bodyIsEmpty(body, locale) {
                const initialBody = this.initialBody(locale);
                const bodyContent = getComposerBodyContent(body);
                const normalizedText = (html) =>
                    this.strippedHtml(html).replaceAll(/\s+/g, ' ').trim();

                return (
                    body === '' ||
                    normalizedText(bodyContent) === '' ||
                    body == initialBody ||
                    normalizedText(body) === normalizedText(initialBody)
                );
            },

            async deleteDraft(
                leadId,
                {
                    waitForDeleteRequest = false,
                    draftVersionAtSend = /** @type {{ id: number, revision: number } | null} */ (
                        null
                    ),
                    videoCleanupBestEffort = false,
                    waitForVideoCleanup = true,
                } = {}
            ) {
                if (leadId) {
                    const deleteRequest = draftVersionAtSend
                        ? this.$axios.delete(`v1/leadxpress/draft/${leadId}`, {
                              params: {
                                  draft_id: draftVersionAtSend.id,
                                  draft_revision: draftVersionAtSend.revision,
                              },
                          })
                        : this.$axios.delete(`v1/leadxpress/draft/${leadId}`);

                    if (waitForDeleteRequest) {
                        await deleteRequest;
                    }
                }

                this.draft = null;

                if (
                    !this.sent &&
                    ['uploading', 'uploaded', 'processed'].includes(this.videoCommunication.status)
                ) {
                    const deleteVideo = async () => {
                        await this.$ziggeo.deleteVideo(
                            this.lead.id,
                            this.videoCommunication.ziggeoId
                        );
                    };

                    if (videoCleanupBestEffort && !waitForVideoCleanup) {
                        deleteVideo().catch(() => undefined);
                        return;
                    }

                    try {
                        await deleteVideo();
                    } catch (error) {
                        if (!videoCleanupBestEffort) {
                            throw error;
                        }
                    }
                }
            },

            clearDraft(leadId) {
                this.deleteDraft(leadId);

                this.reset();
            },

            async discardEmailAiDraft() {
                this.discardingEmailAiDraft = true;

                try {
                    await this.deleteDraft(this.lead?.id, {
                        waitForDeleteRequest: true,
                        videoCleanupBestEffort: true,
                        waitForVideoCleanup: false,
                    });

                    this.skipNextDraftSave = true;
                    this.close();
                } catch (error) {
                    this.$sentry.captureException(error);
                    this.$notify.error(this.$t('toastr.errorNetwork'));
                } finally {
                    this.discardingEmailAiDraft = false;
                }
            },

            isAuthorized(locale) {
                if (this.massMailing) {
                    return (
                        !empty(this.response.subject[locale]) &&
                        !this.bodyIsEmpty(this.response.body[locale], locale) &&
                        (!this.fallbackOwnerShouldBeVisible ||
                            (!this.sent &&
                                empty(this.attachmentErrors) &&
                                !!this.massMailingLeadCount))
                    );
                }

                if (!empty(this.response.template) && this.hasFilteredResponseTemplates) {
                    if (
                        !empty(this.response.subject[locale]) &&
                        !empty(this.response.to) &&
                        !empty(this.response.body[locale]) &&
                        empty(this.attachmentErrors) &&
                        !this.sent
                    ) {
                        return true;
                    }
                }

                if (
                    !empty(this.response.subject[locale]) &&
                    !empty(this.response.to) &&
                    !empty(this.response.body[locale]) &&
                    empty(this.attachmentErrors) &&
                    this.response.cc.every((cc) => this.emailIsValid(cc)) &&
                    this.response.bcc.every((bcc) => this.emailIsValid(bcc)) &&
                    !this.sent
                ) {
                    return true;
                }

                return false;
            },

            setSubject() {
                const prefix = this.isForwarding ? 'Fw:' : 'Re:';
                const sourceSubject = this.activityFeedReplyContext?.sourceSubject;

                if (this.activityFeedReplyContext) {
                    const subject = sourceSubject ? `${prefix} ${sourceSubject}` : prefix;

                    return {
                        fr: subject,
                        en: subject,
                    };
                }

                const communications = this.lead.communications;

                const communication = communications.reverse().find((communication) => {
                    return (
                        !empty(communication.email_subject) &&
                        communication.communication_method_id == CommunicationMethod.EMAIL &&
                        communication.communication_type_id == CommunicationType.INCOMING
                    );
                });

                if (!communication) {
                    return {
                        fr: prefix,
                        en: prefix,
                    };
                }

                return {
                    fr: `Re: ${communication.email_subject}`,
                    en: `Re: ${communication.email_subject}`,
                };
            },

            setInitialBody() {
                this.emailAiDraft?.reset?.();
                this.response.body = {
                    fr: this.initialBody('fr'),
                    en: this.initialBody('en'),
                };
            },

            signature(locale = null) {
                if (!locale) {
                    locale = this.currentLang.toLowerCase();
                }

                const sender = this.effectiveSender;

                if (!sender) {
                    if (!this.isLoadingSenderData) {
                        return '';
                    }

                    return `
                        <div class="signature" data-composer-signature="true">
                            <h4 class="name">
                                <div contentEditable="false" class="max-w-sm w-full">
                                    <div class="flex space-x-4">
                                        <div class="flex-1 space-y-4 py-1">
                                            <div class="h-9 bg-gray-200 rounded w-3/4 mb-3"></div>
                                            <div class="space-y-2">
                                                <div class="h-4 bg-gray-200 rounded mb-2"></div>
                                                <div class="h-4 bg-gray-200 rounded w-5/6"></div>
                                            </div>
                                            ${this.accountLogo(locale)}
                                        </div>
                                    </div>
                                </div>
                            </h4>
                        </div>
                    `;
                }
                const signature = getContentEmailSignature(sender.email_config, locale);

                if (!signature) {
                    return '';
                }

                return `
                    <div class="signature" data-composer-signature="true">
                        ${signature}
                        ${this.accountLogo(locale)}
                    </div>
                `;
            },

            stripSkeletonSignature(html) {
                if (!html) {
                    return html;
                }

                const div = document.createElement('div');
                div.innerHTML = html;
                const signatures = div.querySelectorAll('[data-composer-signature="true"]');

                for (const sig of signatures) {
                    const isSkeleton = !sig.textContent.trim() && sig.querySelector('.bg-gray-200');

                    if (isSkeleton) {
                        sig.remove();
                    }
                }

                return div.innerHTML;
            },

            // Detect when the user manually deletes their signature from the editor so the send
            // flow and signature watchers do not re-insert it. Once a locale's body no
            // longer contains the signature, the removal sticks until the body is rebuilt with one.
            trackSignatureRemoval() {
                for (const locale of ['fr', 'en']) {
                    const signature = this.signaturesByLocale[locale] || '';

                    if (!signature) {
                        continue;
                    }

                    const body = this.response.body[locale] || '';

                    this.signatureRemovedByUser[locale] = !bodyContainsSignature(body, signature);
                }
            },

            parseUserId(value) {
                const id = Number.parseInt(value, 10);

                return Number.isNaN(id) ? null : id;
            },

            accountLogo(locale = null) {
                if (!locale) {
                    locale = this.currentLang.toLowerCase();
                }

                const logo =
                    getContentAccountLogo(this.lead?.account, locale) ||
                    getContentAccountLogo(this.currentUser?.account, locale);

                if (!logo) {
                    return '';
                }

                return `<img src="${logo}" style="max-width: 400px; max-height:125px">`;
            },

            initialBody(locale = null) {
                if (!locale) {
                    locale = this.currentLang.toLowerCase();
                }

                let body = `
                    <div class="body"></div>
                    <p><br><br></p>
                    ${this.signature(locale)}
                `;

                if (this.unsubscribeLink(locale)) {
                    body += `
                        <div data-composer-separator="true"><br><hr></div>
                        ${this.unsubscribeLink(locale)}
                   `;
                }

                body +=
                    '<div class="original-communication" data-composer-original-communication="true"></div>';

                return body;
            },

            addItemsToBody(body, locale = null) {
                const unsubscribeLink = this.unsubscribeLink(locale);

                if (!unsubscribeLink) {
                    return body;
                }

                const container = document.createElement('div');
                container.innerHTML = body;
                const unsubscribeNodes = container.querySelectorAll(
                    '[data-composer-unsubscribe="true"], .unsubscribe-link'
                );

                if (unsubscribeNodes.length) {
                    unsubscribeNodes[0].outerHTML = unsubscribeLink;

                    for (const [index, node] of unsubscribeNodes.entries()) {
                        if (index > 0) {
                            node.remove();
                        }
                    }

                    return container.innerHTML;
                }

                return this.replaceNormalizedUnsubscribe(body, locale);
            },

            replaceNormalizedUnsubscribe(body, locale = null) {
                if (!body.includes('%tag_unsubscribe_url%')) {
                    return `${body}
                        <div data-composer-separator="true"><hr></div>
                        ${this.unsubscribeLink(locale)}
                   `;
                }

                const container = document.createElement('div');
                container.innerHTML = body;

                const childNodes = Array.from(container.childNodes);
                const unsubscribeNodeIndex = childNodes.findIndex(
                    (node) =>
                        (node.textContent || '').includes('%tag_unsubscribe_url%') ||
                        node.outerHTML?.includes('%tag_unsubscribe_url%')
                );

                if (unsubscribeNodeIndex === -1) {
                    return `${body}
                        <div data-composer-separator="true"><hr></div>
                        ${this.unsubscribeLink(locale)}
                   `;
                }

                let removeFromIndex = unsubscribeNodeIndex;

                for (let index = unsubscribeNodeIndex - 1; index >= 0; index -= 1) {
                    const nodeHtml = childNodes[index].outerHTML || '';

                    if (
                        nodeHtml.includes('data-composer-separator="true"') ||
                        nodeHtml.includes('<hr')
                    ) {
                        removeFromIndex = index;
                        break;
                    }
                }

                const prefixContainer = document.createElement('div');

                for (const node of childNodes.slice(0, removeFromIndex)) {
                    prefixContainer.appendChild(node.cloneNode(true));
                }

                return `${prefixContainer.innerHTML}
                        <div data-composer-separator="true"><hr></div>
                        ${this.unsubscribeLink(locale)}
                   `;
            },

            unsubscribeLink(locale = null) {
                if (!locale) {
                    locale = this.currentLang.toLowerCase();
                }

                if (
                    !this.currentUser.email_config ||
                    (this.currentUser.email_config.active && !this.massMailing)
                ) {
                    return '';
                }

                const accountPhone = this.formatPhone(this.currentUser.account.phone, true);

                let unsubscribeText =
                    '<a href="%tag_unsubscribe_url%" style="text-decoration: none;">';

                if (locale === 'en') {
                    unsubscribeText += `
                        Click here</a> to stop receiving communications from ${this.currentUser.account.name} or contact us at ${accountPhone} to be removed from our list.
                    `;
                } else {
                    unsubscribeText += `
                        Cliquez ici</a> pour ne plus recevoir de communications de la part de ${this.currentUser.account.name} ou communiquez avec nous au ${accountPhone} afin d'être retiré de notre liste.
                    `;
                }

                return `
                    <div
                        class="unsubscribe-link"
                        data-composer-unsubscribe="true"
                        style="color: #B0B0B0; font-size: 12px;"
                    >
                        ${unsubscribeText}
                    </div>
                `;
            },

            reset(soft = false) {
                this.emailAiDraft?.reset?.(soft);
                this.response.category = null;
                this.response.template = null;
                this.localePreference = 'leadLocale';
                this.nextStep = 'sendEmail';

                if (!soft) {
                    this.response.subject = {
                        fr: '',
                        en: '',
                    };
                    this.response.attachments = {
                        fr: [],
                        en: [],
                    };
                    this.response.templateAttachments = [];
                    this.response.from = this.currentUser.id;
                    this.response.fromBackup = '';
                    this.response.body = {
                        fr: this.initialBody('fr'),
                        en: this.initialBody('en'),
                    };
                    this.signatureRemovedByUser = { fr: false, en: false };
                    this.response.videoHash = '';
                    this.response.to = [];
                    this.response.cc = [];
                    this.response.bcc = [];
                    this.response.ziggeoId = '';
                    this.response.videoProcessed = false;
                    this.response.design = {
                        fr: '',
                        en: '',
                    };

                    if (this.replyingEmail) {
                        this.response.subject = this.setSubject();
                    }
                }

                this.sent = false;
                this.draft = null;
                this.addLastEmailBody = this.replyingEmail;
                this.$refs.leadXpress?.updateDefault();
            },

            displaySuccessNotification() {
                if (this.hasRecipient) {
                    this.$notify.success(this.$t('email.success_mass_mailing'));
                } else {
                    this.$notify.success(this.$t('email.success'));
                }
            },

            sendWithoutMultilingual() {
                if (this.incompleteLocales.includes('en')) {
                    this.localePreference = 'fr';
                } else {
                    this.localePreference = 'en';
                }

                if (this.nextStep == 'campaignReview') {
                    this.triggerCampaignReview();
                    return;
                }

                this.send(this.lead.id, 0, true);
            },

            fillMissingMultilingualData() {
                const filledLocale = this.currentLang.toLowerCase();
                const toFillLocale = filledLocale === 'fr' ? 'en' : 'fr';

                this.response.subject[toFillLocale] = this.response.subject[filledLocale];
                this.response.body[toFillLocale] = this.response.body[filledLocale];
                this.response.attachments[toFillLocale] = this.response.attachments[filledLocale];
                this.response.design[toFillLocale] = this.response.design[filledLocale];

                this.emailAiDraft?.reset?.(false, toFillLocale);
            },

            async createLandingPage() {
                const landingPageResponse = await this.$ziggeo.createLandingPage(
                    this.lead.id,
                    this.currentUser.id,
                    {
                        title: this.response.subject[this.currentLang.toLowerCase()],
                        leadSid: this.lead.sid,
                        accountSid: this.lead.account.sid,
                    }
                );

                this.landingPageSid = landingPageResponse.data.data.sid;
            },

            async send(leadId, retryCount = 0, useOneLocale = false) {
                if (shouldSkipEmailSend(this, leadId, retryCount)) {
                    return;
                }

                if (shouldShowMissingLocaleModal(this, useOneLocale)) {
                    this.modals.missingLocale.opened = true;
                    return;
                }

                if (this.activityFeedReplyContext && !this.reconcileActivityFeedReplyRecipients()) {
                    return;
                }

                if (!this.massMailing) {
                    this.fillMissingMultilingualData();
                }

                const customerId = Number(this.lead?.customer_id ?? this.lead?.customer?.id);
                this.sending = true;
                this.submitted = true;

                if (!(await prepareEmailVideo(this))) {
                    return;
                }

                const payload = buildEmailPayload(this);

                await submitEmail(this, payload, leadId, customerId);
            },

            multilingualDataComplete() {
                this.incompleteLocales = [];

                if (this.response.body.fr == '' || this.response.subject.fr == '') {
                    this.incompleteLocales.push('fr');
                }

                if (this.response.body.en == '' || this.response.subject.en == '') {
                    this.incompleteLocales.push('en');
                }

                return !this.incompleteLocales.length;
            },

            triggerSave() {
                this.submitted = true;
                this.close();
            },

            triggerCampaignReview(useOneLocale = false) {
                if (
                    this.massMailing &&
                    !this.multilingualDataComplete() &&
                    !useOneLocale &&
                    (this.localePreference === 'leadLocale' ||
                        this.incompleteLocales.includes(this.localePreference))
                ) {
                    this.nextStep = 'campaignReview';
                    this.modals.missingLocale.opened = true;
                    return;
                }

                this.$emit('reviewCampaign', this.emailData);

                this.close();
            },

            changeLanguage(event) {
                this.currentLang = event;
            },

            changeLocale(event) {
                this.localePreference = event;
            },

            isDraftTranslated(draft) {
                return (
                    !!draft.subject_translation.en ||
                    !!draft.subject_translation.fr ||
                    !!draft.body_translation.en ||
                    !!draft.body_translation.fr ||
                    !!draft.design_translation.en ||
                    !!draft.design_translation.fr
                );
            },

            async saveDraft() {
                if (!this.canSaveDraft) {
                    return;
                }

                const payload = {
                    ai_interaction_ids: { ...this.emailAiDraft?.interactionIdByLocale?.value },
                    response_template_custom_category_id: this.response.category?.id,
                    subject: {
                        fr: this.response.subject.fr,
                        en: this.response.subject.en,
                    },
                    to: buildEmailDraftRecipients(
                        this.customerProfileEmailContext,
                        this.response.to
                    ),
                    cc: this.response.cc,
                    bcc: this.response.bcc,
                    from: this.response.from,
                    body: {
                        fr: this.response.body.fr,
                        en: this.response.body.en,
                    },
                    video_processed:
                        this.response.videoProcessed ||
                        this.videoCommunication.status === 'processed',
                    ziggeo_id: this.response.ziggeoId || this.videoCommunication.ziggeoId,
                    design: {
                        fr: this.response.design.fr,
                        en: this.response.design.en,
                    },
                    template: this.response.template,
                    templateAttachments: this.response.templateAttachments,
                    locale: this.currentLang.toLowerCase(),
                };

                try {
                    const draft = await this.$axios.put(
                        `v1/leadxpress/draft/${this.lead.id}`,
                        payload
                    );

                    if (['uploaded', 'processed'].includes(this.videoCommunication.status)) {
                        await this.$ziggeo.updateZiggeoVideo(this.videoCommunication.ziggeoId, {
                            data: {
                                account_sid: this.authUser.account.sid,
                                user_id: this.authUser.id,
                                lead_id: this.lead.id,
                                draft_id: draft.data.data.id,
                            },
                        });
                    }
                } catch (error) {
                    this.appendNewError({
                        code: '0100',
                        display: false,
                        error,
                        payload,
                    });
                }
            },

            async fetchDraft(leadId) {
                try {
                    const response = await this.$axios.get(`v1/leadxpress/draft/${leadId}`);

                    const fetchedDraft = response.data.data;

                    if (!fetchedDraft || fetchedDraft.lead_id != leadId) {
                        return;
                    }

                    const draft = normalizeEmailDraft(fetchedDraft);

                    if (draft.body) {
                        if (
                            draft.body != this.response.body ||
                            !empty(draft.response_template_id)
                        ) {
                            this.draft = draft;
                        }
                    } else if (
                        (draft.body_translation.fr != this.response.body.fr &&
                            draft.body_translation.en != this.response.body.en) ||
                        !empty(draft.response_template_id)
                    ) {
                        this.draft = draft;
                    }

                    this.currentLang = draft.locale.toUpperCase();

                    // Template
                    if (!empty(draft.response_template)) {
                        this.response.templateAttachments = draft.response_template.attachments;

                        this.response.template = draft.response_template;

                        const attachments = {
                            en: [],
                            fr: [],
                        };

                        draft.response_template.translations.forEach((translation) => {
                            const locale = translation.locale;
                            const attachmentArray = attachments[locale];

                            translation.attachments.forEach((attachment) => {
                                attachmentArray.push({
                                    id: attachment.id,
                                    name: attachment.name,
                                    size: attachment.size,
                                    path: attachment.path,
                                });
                            });
                        });

                        this.response.attachments = attachments;
                    }

                    if (draft?.video?.ziggeo_id) {
                        const tempMedia = draft.video;
                        const response = await this.$ziggeo.getVideoStateFromZiggeo(
                            draft.video.ziggeo_id
                        );
                        draft.video = null;

                        if (!response) {
                            return;
                        }

                        response.success(() => {
                            this.response.videoProcessed = draft.video?.video_processed;
                            this.response.ziggeoId = draft.video?.ziggeo_id;
                            draft.video = tempMedia;
                            this.draft.video = tempMedia;
                        });
                    }

                    const isTranslatedDraft = this.isDraftTranslated(draft);

                    // Required to make sure it overwrites template automation
                    this.$nextTick(() => {
                        // for transition period
                        if (draft.division) {
                            this.response.category = draft.division;
                        }

                        if (draft.category) {
                            this.response.category = draft.category;
                        }

                        this.response.to = draft.lead_emails;
                        this.response.cc = draft.cc;
                        this.response.bcc = draft.bcc;
                        this.response.from = parseInt(draft.from, 10) || this.currentUser.id;

                        if (isTranslatedDraft) {
                            if (!this.replyingEmail) {
                                this.response.subject = draft.subject_translation;
                            }

                            this.response.design = draft.design_translation;
                            this.response.body = draft.body_translation;
                        } else {
                            const draftLocale = ['en', 'fr'].includes(draft.locale)
                                ? draft.locale
                                : 'fr';

                            if (!this.replyingEmail) {
                                this.response.subject[draftLocale] = draft.subject;
                            }

                            if (draft.design != null) {
                                this.response.design[draftLocale] = draft.design;
                            }

                            if (draft.body != null) {
                                this.response.body[draftLocale] = draft.body;
                            }
                        }

                        if (this.lead?.id == leadId) {
                            this.emailAiDraft?.restoreInteractionIds?.(draft.ai_interaction_ids);
                        }
                        this.draft = draft;
                    });
                } catch (error) {
                    if (!error.response || error.response.status == 422) {
                        return;
                    }

                    this.appendNewError({
                        code: '0101',
                        display: false,
                        error,
                    });
                }
            },

            async removeCommunicating() {
                if (this.massMailing) {
                    return;
                }

                await this.$api.leads.removeCommunicating(this.query, {
                    blackListedLeadIds: this.allLeadsInTableSelected ? this.blackListedLeadIds : [],
                });
            },

            async setCommunicating(session = this.modalSession, leadId = this.lead?.id) {
                if (!this.$refs.modal?.exists) {
                    return;
                }

                this.communicating = false;

                const response = await this.$api.leads.setCommunicating(this.query, {
                    type: CommunicationMethod.EMAIL,
                    blackListedLeadIds: this.allLeadsInTableSelected ? this.blackListedLeadIds : [],
                });

                if (
                    session !== this.modalSession ||
                    leadId !== this.lead?.id ||
                    !this.$refs.modal?.exists
                ) {
                    return;
                }

                const { data } = response.data;

                if (!this.massMailing) {
                    if (data.user !== false) {
                        this.massMailingLeadsRemoved = null;
                        this.communicating = true;
                        this.communicatingUser = data.user;
                        this.communicatingType = data.type;
                        this.recentCommunication = null;
                    } else if (data.communication !== false) {
                        this.massMailingLeadsRemoved = null;
                        this.communicating = false;
                        this.communicatingUser = null;
                        this.communicatingType = null;
                        this.recentCommunication = data.communication;
                    } else {
                        this.massMailingLeadsRemoved = null;
                        this.communicating = false;
                        this.communicatingUser = null;
                        this.communicatingType = null;
                        this.recentCommunication = null;
                    }
                } else if (
                    Array.isArray(data.leads?.oldCommunicating) &&
                    data.leads?.oldCommunicating.length
                ) {
                    this.massMailingLeadsRemoved = data.leads.oldCommunicating;
                    this.communicating = false;
                    this.communicatingUser = null;
                    this.communicatingType = null;
                    this.recentCommunication = null;
                } else {
                    this.massMailingLeadsRemoved = null;
                    this.communicating = false;
                    this.communicatingUser = null;
                    this.communicatingType = null;
                    this.recentCommunication = null;
                }
            },

            // Check if there has been communication with the email before, if so display options
            async checkOldCommunication() {
                if (!this.$refs.modal?.exists) {
                    return;
                }

                const leadId = this.lead.id;
                const session = this.modalSession;
                const request = ++this.oldCommunicationRequest;
                let response;

                try {
                    response = await this.$axios.get(
                        `v1/leadxpress/check-old-communication/${leadId}`
                    );
                } catch {
                    if (
                        session === this.modalSession &&
                        request === this.oldCommunicationRequest &&
                        leadId === this.lead.id &&
                        this.$refs.modal?.exists
                    ) {
                        this.mostRecentCommunication = null;
                    }

                    return;
                }

                if (
                    session !== this.modalSession ||
                    request !== this.oldCommunicationRequest ||
                    leadId !== this.lead.id ||
                    !this.$refs.modal?.exists
                ) {
                    return;
                }

                this.mostRecentCommunication = empty(response.data.mostRecentCommunication)
                    ? null
                    : response.data.mostRecentCommunication;
            },

            invalidateOldCommunication() {
                this.modalSession += 1;
                this.oldCommunicationRequest += 1;
                this.massMailingLeadsRemoved = null;
                this.communicating = false;
                this.communicatingUser = null;
                this.communicatingType = null;
                this.recentCommunication = null;
                this.mostRecentCommunication = null;
            },

            cancelCampaignCreation() {
                this.eventBus.emit('cancel-campaign-creation');
            },

            dismiss() {
                this.mostRecentCommunication = null;
            },

            onClose() {
                this.invalidateOldCommunication();
                this.removeCommunicating();
                if (this.skipNextDraftSave) {
                    this.skipNextDraftSave = false;
                    return;
                }

                this.saveDraft();
            },

            close() {
                this.invalidateOldCommunication();
                this.$refs.modal.hide();
            },

            async onOpen(leadId, session = this.modalSession) {
                if (leadId && this.massMailing) {
                    this.setAllLeadsInTableSelected(false);
                }

                await this.setLastEmailBody();

                if (session !== this.modalSession) {
                    return;
                }

                this.reset();

                const promises = [];

                promises.push(this.loadResponseTemplates());

                if (!this.massMailing) {
                    this.communicating = true;

                    promises.push(this.setCommunicating(session, this.lead?.id));
                    promises.push(this.checkOldCommunication());
                    promises.push(this.fetchDraft(leadId));
                } else {
                    promises.push(this.setResponseFromCampaignSettings());
                }
                await Promise.all(promises);
            },

            applyActivityFeedReplyContext() {
                if (this.customerProfileEmailContext) {
                    return;
                }
                if (!this.activityFeedReplyContext) {
                    return;
                }

                const selectableLeadEmails = filterActivityFeedEmailRecipients(
                    this.lead.lead_emails || [],
                    this.activityFeedReplyContext.allowedEmails
                );
                const targetAddress = this.activityFeedReplyContext.mismatch
                    ? this.activityFeedReplyContext.preferredEmail
                    : this.activityFeedReplyContext.historicalEmail;
                const normalizedTargetAddress = normalizeEmailAddress(targetAddress);
                const targetEmail = selectableLeadEmails.find(
                    (email) => normalizeEmailAddress(email.email) === normalizedTargetAddress
                );

                this.response.to = targetEmail ? [targetEmail] : [];
            },

            reconcileActivityFeedReplyRecipients() {
                if (this.customerProfileEmailContext) {
                    const allowedIds = new Set(
                        this.customerProfileEmailContext.recipients.map(
                            (recipient) => recipient.contact_email_id
                        )
                    );
                    this.response.to = this.response.to.filter((recipient) =>
                        allowedIds.has(recipient.contact_email_id)
                    );
                    return this.response.to.length > 0;
                }

                const currentEligibleRecipients = filterActivityFeedEmailRecipients(
                    this.lead.lead_emails || [],
                    this.activityFeedReplyContext.allowedEmails
                );
                this.response.to = reconcileActivityFeedEmailRecipients(
                    this.response.to,
                    currentEligibleRecipients
                );

                if (this.response.to.length > 0) {
                    return true;
                }

                this.$notify.error(this.$t('customerProfile.activityFeed.reply.noCurrentEmail'));
                return false;
            },

            onClosed() {
                this.invalidateOldCommunication();
                this.emailAiDraft?.reset?.();

                if (this.submitted && this.sent) {
                    this.massMailingLeadsRemoved = null;
                    this.communicatingUser = null;
                    this.communicatingType = null;
                    this.recentCommunication = null;
                    this.mostRecentCommunication = null;
                    this.communicating = false;
                    this.lastEmailBody = '';
                    this.originalCommunicationHtml = '';

                    if (this.taskEventToBeCompleted) {
                        this.setTaskEventToBeCompleted(null);
                    }

                    // Open Next Step if necessary
                    if (!this.massMailing) {
                        this.eventBus.emit('show-next-step', {
                            lead: this.lead,
                        });
                    }

                    this.setAllLeadsInTableSelected(false);
                } else {
                    this.$emit('canceled', this.triggerSource);
                }

                this.submitted = false;
                this.leadId = null;
                this.aiDraftLeadId = null;
                this.lead = new Lead();
                this.loading = true;
                this.triggerSource = null;
                this.activityFeedReplyContext = null;
                this.customerProfileOutput = null;
                this.customerProfileEmailContext = null;
                this.customerProfileEmailPreselectedIds = [];
                useClientCardStore().replyingEmail = false;
            },

            async onBeforeOpen({
                leadId = null,
                triggerSource = null,
                activityFeedReplyContext = null,
                customerProfileOutput = null,
                customerProfileEmailContext = null,
                customerProfileEmailPreselectedIds = [],
                lead: preparedLead = null,
            }) {
                this.invalidateOldCommunication();
                const session = this.modalSession;
                this.emailAiDraft?.reset?.();
                this.activityFeedReplyContext = activityFeedReplyContext;
                this.customerProfileOutput = customerProfileOutput;
                this.customerProfileEmailContext = customerProfileEmailContext;
                this.customerProfileEmailPreselectedIds = customerProfileEmailPreselectedIds;

                const { run } = useTimedPageAction({
                    action: TimedPageActions.EMAIL_LEAD_MODAL_LOAD,
                    additionalData: {},
                });

                await run(async () => {
                    if (leadId) {
                        this.leadId = leadId;
                        this.aiDraftLeadId = leadId;
                        const lead = await resolveEmailModalLead(
                            leadId,
                            preparedLead,
                            customerProfileOutput,
                            () => this.fetchLead(leadId)
                        );
                        if (session !== this.modalSession) {
                            return;
                        }
                        this.lead = lead;
                    }

                    this.triggerSource = triggerSource;

                    await this.onOpen(leadId, session);
                    if (session !== this.modalSession) {
                        return;
                    }
                    this.applyActivityFeedReplyContext();

                    if (customerProfileEmailContext) {
                        const recipients = buildCustomerEmailRecipients(
                            customerProfileEmailContext,
                            customerProfileEmailPreselectedIds
                        );
                        if (recipients === null) {
                            this.customerProfileEmailContext = {
                                ...customerProfileEmailContext,
                                recipients: [],
                            };
                            this.response.to = [];
                            this.$notify.error(
                                this.$t('customerProfile.actionContext.preflightFailed')
                            );
                            this.loading = false;
                            return;
                        }
                        this.response.to = recipients;
                    }

                    if (customerProfileOutput) {
                        this.response.to = [customerProfileOutput.recipient];
                        this.response.attachments = {
                            fr: [...this.response.attachments.fr, customerProfileOutput.attachment],
                            en: [...this.response.attachments.en, customerProfileOutput.attachment],
                        };
                    }

                    this.currentLang = (
                        customerProfileOutput?.locale ||
                        this.lead.locale ||
                        this.$i18n.getContentLanguage()
                    ).toUpperCase();
                    this.loading = false;
                });
            },

            async setResponseFromCampaignSettings() {
                const response = this.response;
                if (this.campaignSettings?.campaign?.emailData) {
                    response.category = this.campaignSettings.campaign.emailData?.category;
                    response.template = this.campaignSettings.campaign.emailData?.template;
                    response.to = this.campaignSettings.campaign.emailData?.to;
                    response.cc = this.campaignSettings.campaign.emailData?.cc;
                    response.bcc = this.campaignSettings.campaign.emailData?.bcc;
                    response.forwardTo = this.campaignSettings.campaign.emailData?.forwardTo;
                    response.from = this.campaignSettings.campaign.emailData?.from;
                    response.fromBackup = this.campaignSettings.campaign.emailData?.from_backup;
                    response.civility = this.campaignSettings.campaign.emailData?.civility;
                    this.localePreference =
                        this.campaignSettings.campaign.emailData?.localePreference;

                    this.$nextTick(() => {
                        response.attachments =
                            this.campaignSettings.campaign.emailData?.attachments;
                        response.templateAttachments =
                            this.campaignSettings.campaign.emailData?.templateAttachments;
                        response.subject.fr = this.campaignSettings.campaign.emailData.subject.fr;
                        response.subject.en = this.campaignSettings.campaign.emailData.subject.en;
                        response.body.fr = this.campaignSettings.campaign.emailData?.body.fr;
                        response.body.en = this.campaignSettings.campaign.emailData?.body.en;
                        response.design.fr = this.campaignSettings.campaign.emailData?.design.fr;
                        response.design.en = this.campaignSettings.campaign.emailData?.design.en;
                        this.response = response;
                    });
                }
            },

            async loadResponseTemplates() {
                const account = this.lead.account_id ? this.lead.account : this.contextAccount;

                if (!account._response_templates.length) {
                    account.response_templates = await this.$api.templates.index(account.id);
                }
            },
        },

        beforeUnmount() {
            this.invalidateOldCommunication();
        },
    };
</script>
